import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';

import { Button, CHANNELS } from '@spotahome/ui-library';
import ListingChipsContainer from '@spotahome/ui-library/src/tenant/components/ListingChipsContainer';
import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import { formatFeaturesToChipList } from '@spotahome/ui-library/src/tenant/components/PropertyRulesRedesign/utils';

import { ListingSectionObservable } from '../ListingSection';

import openContractBasicsSimplifiedModal from './ContractBasicsSimplifiedModal';

import './ContractBasicsSimplified.scss';

const MAX_VISIBLE_CHIPS = 8;

const formatDocumentsToChipListSimplified = documentsPolicy => {
  if (!documentsPolicy?.requiredDocumentation?.documents?.length) return [];
  return documentsPolicy.requiredDocumentation.documents.map(documentKey => {
    const documentType = documentKey.replace('property.rule.document.', '');
    return {
      name: trans(
        `listing.simplification.bottom.contract-basics.document.${documentType}`
      ),
      value: true,
      icon: 'description'
    };
  });
};

const openContractTypeModal = contractType => () => {
  Mediator.publish(CHANNELS.OPEN_INFORMATION_MODAL, {
    title: trans('listing.pricing.details.type-of-contract'),
    body: (
      <>
        <p>
          <strong>{trans(`contract_type.label.${contractType}`)}</strong>
        </p>
        <p>{trans(`contract_type.descripition.${contractType}`)}</p>
      </>
    )
  });
};

const buildContractTypeChip = contractType =>
  contractType
    ? {
        name: `${trans('listing.pricing.details.type-of-contract')}: ${trans(`contract_type.label.${contractType}`)}`,
        icon: 'receipt_long',
        value: true,
        onClick: openContractTypeModal(contractType)
      }
    : null;

const buildMinStayChip = minStay =>
  minStay
    ? {
        name: trans(
          'listing.simplification.bottom.contract-basics.min-stay.chip',
          { timeInDays: minStay }
        ),
        icon: 'date_range',
        value: true
      }
    : null;

const ContractBasicsSimplified = ({
  contractualPermissions,
  contractType,
  documentsPolicy,
  minStay,
  currency
}) => {
  const contractualPermissionsChips = contractualPermissions
    ? formatFeaturesToChipList(contractualPermissions, currency).filter(Boolean)
    : [];

  const documentsPolicyChips = formatDocumentsToChipListSimplified(documentsPolicy);

  const chips = [
    ...contractualPermissionsChips,
    buildContractTypeChip(contractType),
    buildMinStayChip(minStay),
    ...documentsPolicyChips
  ].filter(Boolean);

  if (!chips.length) {
    return null;
  }

  return (
    <ListingSectionObservable
      section="contractBasics"
      className="contract-basics-simplified"
    >
      <ListingTitle role="secondary">
        {trans('listing.simplification.bottom.contract-basics.card.title')}
      </ListingTitle>
      <ListingChipsContainer
        chipList={chips}
        maxVisibleChips={MAX_VISIBLE_CHIPS}
        showShowMoreButton={false}
      />
      <Button
        type="button"
        color="link"
        className="contract-basics-simplified__view-more"
        onClick={() =>
          openContractBasicsSimplifiedModal({
            chips,
            documentsAdditionalInfo:
              documentsPolicy?.requiredDocumentation?.additionalInfo,
            fullscreenOnMobile: true
          })
        }
      >
        {trans('common.view-more')}
      </Button>
    </ListingSectionObservable>
  );
};

ContractBasicsSimplified.propTypes = {
  contractualPermissions: PropTypes.object,
  contractType: PropTypes.string,
  documentsPolicy: PropTypes.object,
  minStay: PropTypes.number,
  currency: PropTypes.string.isRequired
};

export default ContractBasicsSimplified;
